import React from 'react';
import styled from 'styled-components';

interface LinkIconProps {
  type: 'github' | 'scholar' | 'kaggle' | 'demo' | 'play';
  size?: number;
}

const IconContainer = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  perspective: 1000px;
  position: relative;
  cursor: pointer;
  display: inline-block;
  
  /* Mobile optimization */
  @media (max-width: 768px) {
    width: ${props => props.size * 0.9}px;
    height: ${props => props.size * 0.9}px;
    perspective: 800px;
  }
  
  @media (max-width: 480px) {
    width: ${props => props.size * 0.85}px;
    height: ${props => props.size * 0.85}px;
    perspective: 600px;
  }
  
  /* Reduce motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    .link-icon-3d {
      transition: none !important;
      
      &:hover {
        transform: scale(1.05) !important;
      }
    }
  }
  
  .link-icon-3d {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    
    /* Stunning hover with shine and bounce */
    &:hover {
      transform: translateY(-8px) rotateY(360deg) scale(1.15);
      filter: drop-shadow(0 10px 20px rgba(99, 102, 241, 0.5));
      
      .icon-shine {
        opacity: 1;
        animation: shine 1.5s ease-in-out infinite;
      }
      
      .icon-glow {
        opacity: 1;
        transform: scale(1.5);
      }
      
      .icon-ring {
        opacity: 1;
        animation: expandRing 1s ease-out infinite;
      }
    }
    
    /* Mobile: Simpler hover */
    @media (max-width: 768px) {
      &:hover {
        transform: translateY(-4px) scale(1.1);
      }
    }
  }
  
  .icon-layer {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .icon-glow {
    position: absolute;
    width: 120%;
    height: 120%;
    border-radius: 50%;
    filter: blur(15px);
    opacity: 0;
    transition: all 0.5s ease;
    z-index: -1;
  }
  
  .icon-shine {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }
  
  .icon-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
  }
  
  @keyframes shine {
    0%, 100% {
      transform: translateX(-100%) rotate(45deg);
    }
    50% {
      transform: translateX(100%) rotate(45deg);
    }
  }
  
  @keyframes expandRing {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    100% {
      transform: scale(1.8);
      opacity: 0;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// GitHub Icon - Octocat with 3D effect
const GitHubIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(88, 166, 255, 0.8) 0%, transparent 70%)' 
    }} />
    <div className="icon-ring">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" stroke="#58a6ff" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="githubGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#58a6ff', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#1f6feb', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="githubShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" floodColor="#1f6feb"/>
          </filter>
        </defs>
        {/* GitHub Octocat silhouette */}
        <circle cx="32" cy="32" r="28" fill="url(#githubGrad)" filter="url(#githubShadow)" />
        <path d="M 32 12 C 20.95 12 12 20.95 12 32 C 12 40.84 17.34 48.37 24.84 51.02 C 25.84 51.19 26.21 50.57 26.21 50.03 C 26.21 49.54 26.19 48.06 26.19 46.52 C 21 47.69 19.84 44.29 19.84 44.29 C 18.94 41.99 17.63 41.38 17.63 41.38 C 15.82 40.18 17.77 40.2 17.77 40.2 C 19.77 40.34 20.82 42.23 20.82 42.23 C 22.6 45.25 25.46 44.37 26.24 44.08 C 26.43 42.79 26.94 41.91 27.5 41.63 C 23.48 41.34 19.26 39.75 19.26 32.44 C 19.26 30.25 20.01 28.47 20.86 27.07 C 20.65 26.78 20 24.9 21.06 22.35 C 21.06 22.35 22.75 21.8 26.18 24.11 C 27.77 23.67 29.39 23.45 31 23.44 C 32.61 23.45 34.23 23.67 35.82 24.11 C 39.25 21.8 40.94 22.35 40.94 22.35 C 42 24.9 41.35 26.78 41.14 27.07 C 41.99 28.47 42.74 30.25 42.74 32.44 C 42.74 39.77 38.5 41.33 34.47 41.61 C 35.17 42.22 35.8 43.41 35.8 45.24 C 35.8 47.82 35.78 49.89 35.78 50.03 C 35.78 50.58 36.14 51.2 37.16 51.02 C 44.66 48.36 50 40.84 50 32 C 50 20.95 41.05 12 32 12 Z" 
              fill="white" fillOpacity="0.95" />
      </svg>
    </div>
    <div className="icon-shine">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <rect x="0" y="0" width="20" height="100" fill="white" fillOpacity="0.3" />
      </svg>
    </div>
  </>
);

// Google Scholar Icon - Graduation cap with academic theme
const ScholarIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(66, 133, 244, 0.8) 0%, transparent 70%)' 
    }} />
    <div className="icon-ring">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" stroke="#4285f4" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="scholarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#4285f4', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#1a73e8', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="scholarShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" floodColor="#1a73e8"/>
          </filter>
        </defs>
        {/* Scholar background circle */}
        <circle cx="32" cy="32" r="28" fill="url(#scholarGrad)" filter="url(#scholarShadow)" />
        {/* Graduation cap */}
        <path d="M 32 18 L 12 26 L 32 34 L 52 26 Z" fill="white" fillOpacity="0.95" />
        <path d="M 50 26 L 50 36 C 50 38 42 42 32 42 C 22 42 14 38 14 36 L 14 26" 
              stroke="white" strokeWidth="2" fill="none" strokeOpacity="0.9" strokeLinecap="round" />
        {/* Cap top */}
        <rect x="30" y="14" width="4" height="4" fill="white" fillOpacity="0.95" />
        {/* Book */}
        <rect x="24" y="44" width="16" height="10" rx="1" fill="white" fillOpacity="0.8" />
        <line x1="24" y1="48" x2="40" y2="48" stroke="#4285f4" strokeWidth="1" />
        <line x1="24" y1="51" x2="40" y2="51" stroke="#4285f4" strokeWidth="1" />
        {/* Sparkle effect */}
        <circle cx="48" cy="20" r="1.5" fill="white" opacity="0.9">
          <animate attributeName="opacity" values="0.9;0.3;0.9" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="38" r="1.5" fill="white" opacity="0.7">
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="2s" begin="0.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
    <div className="icon-shine">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <rect x="0" y="0" width="20" height="100" fill="white" fillOpacity="0.3" />
      </svg>
    </div>
  </>
);

// Kaggle Icon - Data science theme
const KaggleIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(32, 201, 255, 0.8) 0%, transparent 70%)' 
    }} />
    <div className="icon-ring">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" stroke="#20c9ff" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="kaggleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#20c9ff', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#4fc3f7', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="kaggleShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" floodColor="#20c9ff"/>
          </filter>
        </defs>
        {/* Kaggle background */}
        <circle cx="32" cy="32" r="28" fill="url(#kaggleGrad)" filter="url(#kaggleShadow)" />
        {/* Kaggle K logo approximation */}
        <path d="M 20 16 L 20 48 L 26 48 L 26 34 L 32 28 L 42 48 L 50 48 L 37 24 L 48 16 L 40 16 L 26 27 L 26 16 Z" 
              fill="white" fillOpacity="0.95" />
        {/* Data particles */}
        <circle cx="16" cy="20" r="2" fill="white" opacity="0.6">
          <animate attributeName="cy" values="20;24;20" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="48" cy="40" r="2" fill="white" opacity="0.5">
          <animate attributeName="cy" values="40;36;40" dur="2s" begin="0.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="52" cy="24" r="1.5" fill="white" opacity="0.7">
          <animate attributeName="cy" values="24;28;24" dur="2s" begin="1s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
    <div className="icon-shine">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <rect x="0" y="0" width="20" height="100" fill="white" fillOpacity="0.3" />
      </svg>
    </div>
  </>
);

// Demo/External Link Icon - Rocket launching
const DemoIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.8) 0%, transparent 70%)' 
    }} />
    <div className="icon-ring">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" stroke="#8b5cf6" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="demoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="demoShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" floodColor="#6366f1"/>
          </filter>
        </defs>
        {/* Demo background */}
        <circle cx="32" cy="32" r="28" fill="url(#demoGrad)" filter="url(#demoShadow)" />
        {/* Rocket body */}
        <ellipse cx="32" cy="30" rx="8" ry="16" fill="white" fillOpacity="0.95" />
        {/* Rocket window */}
        <circle cx="32" cy="24" r="4" fill="#8b5cf6" />
        <circle cx="32" cy="24" r="2" fill="white" fillOpacity="0.8" />
        {/* Rocket fins */}
        <path d="M 24 38 L 20 46 L 28 42 Z" fill="white" fillOpacity="0.9" />
        <path d="M 40 38 L 44 46 L 36 42 Z" fill="white" fillOpacity="0.9" />
        {/* Rocket flames */}
        <path d="M 28 46 Q 32 52, 36 46" fill="#ff6b6b" fillOpacity="0.8">
          <animate attributeName="d" 
            values="M 28 46 Q 32 52, 36 46;M 28 46 Q 32 50, 36 46;M 28 46 Q 32 52, 36 46" 
            dur="0.5s" repeatCount="indefinite" />
        </path>
        <path d="M 29 48 Q 32 54, 35 48" fill="#ffd93d" fillOpacity="0.9">
          <animate attributeName="d" 
            values="M 29 48 Q 32 54, 35 48;M 29 48 Q 32 52, 35 48;M 29 48 Q 32 54, 35 48" 
            dur="0.5s" repeatCount="indefinite" />
        </path>
        {/* Stars */}
        <circle cx="16" cy="16" r="1.5" fill="white" opacity="0.9">
          <animate attributeName="opacity" values="0.9;0.3;0.9" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="48" cy="18" r="1" fill="white" opacity="0.7">
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="52" cy="44" r="1.5" fill="white" opacity="0.8">
          <animate attributeName="opacity" values="0.8;0.3;0.8" dur="1.5s" begin="1s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
    <div className="icon-shine">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <rect x="0" y="0" width="20" height="100" fill="white" fillOpacity="0.3" />
      </svg>
    </div>
  </>
);

// Play/Download Icon - Play button with pulse
const PlayIcon = () => (
  <>
    <div className="icon-glow" style={{ 
      background: 'radial-gradient(circle, rgba(16, 185, 129, 0.8) 0%, transparent 70%)' 
    }} />
    <div className="icon-ring">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" stroke="#10b981" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="icon-layer">
      <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
        <defs>
          <linearGradient id="playGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#059669', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="playShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" floodColor="#059669"/>
          </filter>
        </defs>
        {/* Play background */}
        <circle cx="32" cy="32" r="28" fill="url(#playGrad)" filter="url(#playShadow)" />
        {/* Play button triangle */}
        <path d="M 26 20 L 26 44 L 46 32 Z" fill="white" fillOpacity="0.95">
          <animate attributeName="opacity" values="0.95;0.7;0.95" dur="2s" repeatCount="indefinite" />
        </path>
        {/* Pulse rings */}
        <circle cx="32" cy="32" r="20" stroke="white" strokeWidth="2" fill="none" strokeOpacity="0.4">
          <animate attributeName="r" values="20;24;20" dur="2s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
        </circle>
        {/* Download arrow hint */}
        <path d="M 38 52 L 32 56 L 26 52" stroke="white" strokeWidth="2" fill="none" strokeOpacity="0.6" strokeLinecap="round">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>
    <div className="icon-shine">
      <svg width="100%" height="100%" viewBox="0 0 64 64">
        <rect x="0" y="0" width="20" height="100" fill="white" fillOpacity="0.3" />
      </svg>
    </div>
  </>
);

const iconComponents = {
  github: GitHubIcon,
  scholar: ScholarIcon,
  kaggle: KaggleIcon,
  demo: DemoIcon,
  play: PlayIcon
};

export const LinkIcon: React.FC<LinkIconProps> = ({ type, size = 24 }) => {
  const IconComponent = iconComponents[type];
  
  return (
    <IconContainer size={size}>
      <div className="link-icon-3d">
        <IconComponent />
      </div>
    </IconContainer>
  );
};

